import {
    ClassicyApp,
    ClassicyButton,
    ClassicyCheckbox,
    ClassicyIcons,
    ClassicyWindow,
    intToHex,
    quitMenuItemHelper,
    registerClassicyIcons,
    useAppManager,
    useAppManagerDispatch,
    useClassicyDateTime,
} from "classicy";
import { manifestDescription } from "../../Components/manifestDescription";
import appIconPng from "./app.png";
import type React from "react";
import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    useSyncExternalStore,
} from "react";
import { useAboutApp } from "../../Components/AboutApp/AboutApp";
import { MediaStreamContext } from "../../Providers/MediaStream/MediaStreamContext";
import type { MediaItem } from "../../Providers/MediaStream/MediaStreamContext";
import { FocusedItemPlayer } from "./FocusedItemPlayer";
import { NowPlayingList } from "./NowPlayingList";
import styles from "./RadioScanner.module.scss";
import "./RadioScannerContext";
import type { RadioRemoteCommand } from "./RadioScannerContext";
import { trackAppToggle } from "../../openreplay";
import { isAudioBlocked, subscribeAudioBlocked } from "./audioBlocked";
import {
    effectiveMutedIds,
    sanitizeActiveStation,
    sanitizeItemIds,
} from "./radioPlayback";
import {
    DEFAULT_RADIO_SCANNER_SETTINGS,
    nextVizMode,
    radioScannerSetSettings,
    type RadioScannerSettings,
    readRadioScannerSettings,
} from "./radioScannerSettings";
import { RadioSettingsWindow } from "./RadioSettingsWindow";
import { StationPlayer } from "./StationPlayer";
import {
    activeSegments,
    BROADCAST_STATIONS,
    combinedPrevious,
    combinedUpcoming,
    countdownLabel,
    mergeWithSources,
    previousSegments,
    sortStations,
    type Station,
    startTimeLabel,
    stationStatus,
    upcomingSegments,
} from "./stationGrouping";
import { StationButtonContent } from "./StationButtonContent";

type RadioScannerProps = Record<string, never>;

// The "All Traffic" pseudo-station: a synthetic station, appended to the end of
// the strip, that aggregates every station into one view and mixes their audio
// together — a scanner "monitor everything" mode. (The continuous broadcast
// stations live in the Radio Tuner app, not here — see BROADCAST_STATIONS.)
// The key is a sentinel that can never collide with a real source name.
const ALL_TRAFFIC_KEY = "__all_traffic__";
const ALL_TRAFFIC_LABEL = "All Traffic";

// This app's own icon, registered into the shared registry at
// ClassicyIcons.applications.radioScanner.app. registerClassicyIcons assigns
// shallowly, so the existing applications namespace is spread in to keep
// classicy's bundled app icons intact (same gotcha as FlightTracker.tsx).
// classicy's bundled `radio` icon stays untouched — the Radio Tuner uses it.
const ICONS = registerClassicyIcons({
    applications: {
        ...ClassicyIcons.applications,
        radioScanner: { app: appIconPng },
    },
});

export const RadioScanner: React.FC<RadioScannerProps> = () => {
    const appName = "Radio Scanner";
    const appId = "RadioScanner.app";
    const appIcon = ICONS.applications.radioScanner.app as string;
    const aboutWindow = useAboutApp(appId, appIcon);

    const desktopEventDispatch = useAppManagerDispatch();
    const appState = useAppManager(
        (state) =>
            state.System.Manager.Applications.apps[appId]?.data as
            | Record<string, unknown>
            | undefined,
    );

    // Waveform preferences — persisted under data.settings, distinct from the
    // SetState fields below. The overlay toggle and the Settings window both
    // write through saveSettings.
    const settings = useMemo(
        () => readRadioScannerSettings(appState),
        [appState],
    );

    const saveSettings = useCallback(
        (next: RadioScannerSettings) =>
            desktopEventDispatch(radioScannerSetSettings(next)),
        [desktopEventDispatch],
    );

    const onCycleVizMode = useCallback(() => {
        saveSettings({ ...settings, vizMode: nextVizMode(settings.vizMode) });
    }, [saveSettings, settings]);

    const waveColors = useMemo(
        () =>
            settings.useThemeColors
                ? null
                : {
                    bright: intToHex(settings.colorBright),
                    dim: intToHex(settings.colorDim),
                },
        [settings],
    );

    // Settings store percent (slider-native); players take a 0..1 fraction.
    const maxVolume = settings.maxVolume / 100;

    // Settings window draft (TimeMachine pattern): seeded from the persisted
    // settings on open, dispatched only on Save.
    const [showSettings, setShowSettings] = useState(false);
    const [settingsForm, setSettingsForm] = useState<RadioScannerSettings>(
        DEFAULT_RADIO_SCANNER_SETTINGS,
    );
    const openSettings = useCallback(() => {
        setSettingsForm(settings);
        setShowSettings(true);
    }, [settings]);
    const saveSettingsForm = useCallback(() => {
        saveSettings(settingsForm);
        setShowSettings(false);
    }, [saveSettings, settingsForm]);

    const isOpen = useAppManager(
        (state) => state.System.Manager.Applications.apps[appId]?.open ?? false,
    );
    const prevIsOpenRef = useRef<boolean | undefined>(undefined);
    useEffect(() => {
        if (prevIsOpenRef.current === undefined) {
            prevIsOpenRef.current = isOpen;
            return;
        }
        if (prevIsOpenRef.current === isOpen) return;
        prevIsOpenRef.current = isOpen;
        trackAppToggle(appId, isOpen ? "open" : "close");
    }, [isOpen]);

    const {
        mp3Items: items,
        mp3History,
        subscribeMp3,
        unsubscribeMp3,
        sources,
        getUpcomingMp3Items,
    } = useContext(MediaStreamContext);
    // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally mount-only
    useEffect(() => {
        subscribeMp3(appId);
        return () => unsubscribeMp3(appId);
    }, [subscribeMp3, unsubscribeMp3, appId]);

    const { dateTime, paused: clockPaused, tzOffset } = useClassicyDateTime();

    const [captionsOn, setCaptionsOn] = useState<boolean>(false);
    const [activeStation, setActiveStation] = useState<string>(
        sanitizeActiveStation(appState?.activeStation),
    );
    const [mutedItems, setMutedItems] = useState<number[]>(
        sanitizeItemIds(appState?.mutedItems),
    );
    const [showWaveform, setShowWaveform] = useState<boolean>(
        (appState?.showWaveform as boolean) ?? true,
    );
    const [focusedItem, setFocusedItem] = useState<MediaItem | null>(null);
    // Solo (ephemeral, not persisted): while set, every other playing item is
    // muted via effectiveMutedIds and the now-playing marquee pauses. Manual
    // mutedItems stay untouched, so un-soloing restores them exactly.
    const [soloItemId, setSoloItemId] = useState<number | null>(null);

    // Accumulate all mp3Items ever received so previousSegments can access them
    // even after they expire from the live stream.
    const seenItemsRef = useRef<Map<number, MediaItem>>(new Map());
    useEffect(() => {
        for (const item of items) {
            seenItemsRef.current.set(item.id, item);
        }
    }, [items]);

    // Fine virtual clock: the stored dateTime advances per minute, so add the
    // real time elapsed since its last update to recover sub-minute precision.
    const dateTimeRef = useRef(dateTime);
    dateTimeRef.current = dateTime;
    const clockPausedRef = useRef(clockPaused);
    clockPausedRef.current = clockPaused;
    const dateTimeUpdatedAtRef = useRef<number>(Date.now());
    // biome-ignore lint/correctness/useExhaustiveDependencies: trigger-only dep
    useEffect(() => {
        dateTimeUpdatedAtRef.current = Date.now();
    }, [dateTime]);

    const getNowMs = useCallback(() => {
        const elapsed = clockPausedRef.current
            ? 0
            : Date.now() - dateTimeUpdatedAtRef.current;
        return new Date(dateTimeRef.current).getTime() + elapsed;
    }, []);
    const nowMs = getNowMs();

    // Re-render every second so nowMs tracks the clock at ~1s resolution.
    const [tick, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick((n) => n + 1), 1000);
        return () => clearInterval(id);
    }, []);

    // The scanner's stations are the comm-traffic sources only — the continuous
    // broadcast stations (WCBS/WINS) belong to the Radio Tuner app.
    const stations = useMemo(
        () =>
            mergeWithSources(sources.audio, items).filter(
                (s) => !BROADCAST_STATIONS.has(s.key),
            ),
        [sources.audio, items],
    );

    // All Traffic view: which channels the user has switched OFF. Stored
    // as the disabled set (ephemeral, not persisted) so channels that appear
    // later default to ON. Toggling a checkbox flips membership.
    const [disabledTrafficChannels, setDisabledTrafficChannels] = useState<
        Set<string>
    >(() => new Set());
    const setTrafficChannelEnabled = useCallback(
        (key: string, enabled: boolean) =>
            setDisabledTrafficChannels((prev) => {
                const next = new Set(prev);
                if (enabled) next.delete(key);
                else next.add(key);
                return next;
            }),
        [],
    );
    const enabledTrafficStations = useMemo(
        () => stations.filter((s) => !disabledTrafficChannels.has(s.key)),
        [stations, disabledTrafficChannels],
    );
    const isAllTraffic = activeStation === ALL_TRAFFIC_KEY;

    // Snapshot of items waiting in the reveal buffer — refreshed every second.
    // biome-ignore lint/correctness/useExhaustiveDependencies: tick is the intended dependency
    const upcomingItems = useMemo(
        () => getUpcomingMp3Items(),
        [tick, getUpcomingMp3Items], // eslint-disable-line react-hooks/exhaustive-deps
    );

    // Select the first station once stations arrive. A persisted activeStation
    // may be a broadcast station from before the Radio Tuner split — those no
    // longer exist here, so fall back to the first station too.
    useEffect(() => {
        if (
            (activeStation === "" || BROADCAST_STATIONS.has(activeStation)) &&
            stations.length > 0
        ) {
            setActiveStation(stations[0].key);
        }
    }, [stations, activeStation]);

    // Persist state on every change.
    useEffect(() => {
        desktopEventDispatch({
            type: "ClassicyAppRadioScannerSetState",
            activeStation,
            mutedItems,
            showWaveform,
        });
    }, [activeStation, mutedItems, showWaveform, desktopEventDispatch]);

    // Apply each remote tune command (playlist engine, etc.) exactly once,
    // tracked by its monotonic seq — TV.tsx's command pattern. If the station
    // isn't in the list yet, the seq is left unconsumed so the effect retries
    // when `stations` updates. Drives the same local path a station-button
    // click uses (audio-unlock semantics included).
    const command = appState?.command as RadioRemoteCommand | undefined;
    const lastCommandSeqRef = useRef(0);
    useEffect(() => {
        if (!command || command.seq <= lastCommandSeqRef.current) return;
        if (command.kind !== "tune") {
            lastCommandSeqRef.current = command.seq;
            return;
        }
        const lc = command.station.toLowerCase();
        const match = stations.find((s) => s.key.toLowerCase() === lc);
        if (!match) return; // not in the list yet — retry on next update
        lastCommandSeqRef.current = command.seq;
        setActiveStation(match.key);
        setFocusedItem(null);
    }, [command, stations]);

    const toggleItemMute = (id: number) => {
        setMutedItems((prev) =>
            prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
        );
    };

    const toggleSoloItem = useCallback((id: number) => {
        setSoloItemId((prev) => (prev === id ? null : id));
    }, []);

    // Solo is scoped to the station it was started on.
    // biome-ignore lint/correctness/useExhaustiveDependencies: reset-on-change effect
    useEffect(() => {
        setSoloItemId(null);
    }, [activeStation]);

    const appMenu = [
        {
            id: "file",
            title: "File",
            menuChildren: [
                {
                    id: "settings",
                    title: "Settings…",
                    onClickFunc: openSettings,
                },
                quitMenuItemHelper(appId, appName, appIcon),
            ],
        },
        {
            id: "view",
            title: "View",
            menuChildren: [
                {
                    id: "toggle-waveform",
                    title: `${showWaveform ? "✓ " : "  "}Show Waveform`,
                    onClickFunc: () => setShowWaveform((v) => !v),
                },
            ],
        },
    ];

    // The "All Traffic" aggregate: a synthetic station whose items are every
    // ENABLED station's items combined. activeSegments/StationPlayer
    // then treat it exactly like a real station — mixing all of their audio and
    // listing every in-window clip. Memoized so its identity is stable for the
    // playingSegments memo and StationPlayer's volume effect.
    const allTrafficStation = useMemo<Station>(
        () => ({
            key: ALL_TRAFFIC_KEY,
            label: ALL_TRAFFIC_LABEL,
            items: enabledTrafficStations.flatMap((s) => s.items),
        }),
        [enabledTrafficStations],
    );

    const activeStationObj = isAllTraffic
        ? allTrafficStation
        : stations.find((s) => s.key === activeStation);

    // The active station's in-window segments — shared by the now-playing
    // list, the solo lifecycle, and the effective-mute derivation. Memoized so
    // the no-solo playerMutedItems keeps mutedItems' identity and the volume
    // effect in StationPlayer doesn't re-fire on unrelated renders.
    // biome-ignore lint/correctness/useExhaustiveDependencies: nowMs is the clock dep
    const playingSegments = useMemo(
        () => (activeStationObj ? activeSegments(activeStationObj, nowMs) : []),
        [activeStationObj, nowMs],
    );

    // A soloed clip that finishes (or expires on seek) releases the solo, so
    // the rest of the mix comes back rather than staying silent.
    useEffect(() => {
        if (
            soloItemId !== null &&
            !playingSegments.some((i) => i.id === soloItemId)
        ) {
            setSoloItemId(null);
        }
    }, [playingSegments, soloItemId]);

    // What the audio elements actually honor: manual mutes, or — while a solo
    // is active — everything in the mix except the soloed item.
    const playerMutedItems = useMemo(
        () =>
            effectiveMutedIds(
                mutedItems,
                soloItemId,
                playingSegments.map((i) => i.id),
            ),
        [mutedItems, soloItemId, playingSegments],
    );

    const showSchedule = activeStation !== "";

    // True while the browser's autoplay policy is holding audio back (Safari
    // refuses gesture-less sound on page load). The document-level unlock
    // listeners in audioCapture.ts / StationPlayer.tsx make any click the fix,
    // so the overlay below only has to ask for one.
    const audioBlocked = useSyncExternalStore(
        subscribeAudioBlocked,
        isAudioBlocked,
    );

    // Previous = the server's full back-catalogue (everything started before the
    // snapshot instant) plus items seen live since (which cover the gap between
    // history snapshots). Later-seen copies win the id merge; the segment helpers
    // keep only entries that have actually ended by nowMs. Built fresh each render
    // (not memoized) so it always reflects the latest seenItemsRef mutations.
    const historyPool = Array.from(
        new Map(
            [...mp3History, ...seenItemsRef.current.values()].map((i) => [i.id, i]),
        ).values(),
    );

    // Coming Up / Previous: for a single station, filter to that station; for the
    // All Traffic view, merge across every enabled non-live station.
    const upcomingList = !showSchedule
        ? []
        : isAllTraffic
            ? combinedUpcoming(enabledTrafficStations, upcomingItems, nowMs)
            : activeStationObj
                ? upcomingSegments(activeStationObj, upcomingItems, nowMs)
                : [];
    const previousList = !showSchedule
        ? []
        : isAllTraffic
            ? combinedPrevious(enabledTrafficStations, historyPool, nowMs)
            : activeStationObj
                ? previousSegments(activeStationObj, historyPool, nowMs)
                : [];

    // Strip order: pinned live stations, then on-air, upcoming, offline.
    const sortedStations = useMemo(
        () => sortStations(stations, upcomingItems, nowMs),
        [stations, upcomingItems, nowMs],
    );

    // Indicator light for the All Traffic button: on-air if ANY station is
    // playing now, upcoming if any has a queued item, else offline. Uses all
    // stations (not just enabled ones) so the light reflects available
    // traffic regardless of the user's channel filter.
    const allTrafficStatus = stations.some(
        (s) => activeSegments(s, nowMs).length > 0,
    )
        ? ("on-air" as const)
        : combinedUpcoming(stations, upcomingItems, nowMs).length > 0
            ? ("upcoming" as const)
            : ("offline" as const);

    return (
        <ClassicyApp
            id={appId}
            name={appName}
            icon={appIcon}
            defaultWindow={`${appId}_main`}
            desktopIconBalloonHelp={manifestDescription(appId)}
        >
            {showSettings && (
                <RadioSettingsWindow
                    appId={appId}
                    appIcon={appIcon}
                    appMenu={appMenu}
                    idPrefix="radioscanner_settings"
                    form={settingsForm}
                    setForm={setSettingsForm}
                    onCancel={() => setShowSettings(false)}
                    onSave={saveSettingsForm}
                />
            )}
            <ClassicyWindow
                id={`${appId}_main`}
                title={appName}
                appId={appId}
                icon={appIcon}
                closable={true}
                resizable={true}
                zoomable={true}
                scrollable={false}
                collapsable={true}
                initialSize={["50%", "50%"]}
                initialPosition={["left", "top"]}
                minimumSize={[500, 280]}
                modal={false}
                appMenu={appMenu}
            >
                <div className={styles.rsContainer}>
                    {audioBlocked && (
                        <div className={styles.rsUnlockOverlay}>
                            <div className={styles.rsUnlockOverlayBox}>
                                <p className={styles.rsUnlockOverlayTitle}>
                                    Click anywhere to start audio
                                </p>
                                <p className={styles.rsUnlockOverlayHint}>
                                    Your browser paused sound until you
                                    interact with the page
                                </p>
                            </div>
                        </div>
                    )}
                    <div className={styles.rsMainArea}>
                        {focusedItem ? (
                            <FocusedItemPlayer
                                item={focusedItem}
                                onDismiss={() => setFocusedItem(null)}
                                showWaveform={showWaveform}
                                captionsOn={captionsOn}
                                captionStyle={settings.captionStyle}
                                vizMode={settings.vizMode}
                                onCycleVizMode={onCycleVizMode}
                                waveColors={waveColors}
                                maxVolume={maxVolume}
                                playOriginalAudio={settings.playOriginalAudio}
                            />
                        ) : (
                            activeStationObj && (
                                <>
                                    <div className={styles.rsDisplay}>
                                        <p className={styles.rsDisplaySource}>
                                            {activeStationObj.label}
                                        </p>
                                        {isAllTraffic && (
                                            <div className={styles.rsTrafficChannels}>
                                                <p className={styles.rsScheduleLabel}>
                                                    Channels
                                                </p>
                                                <div
                                                    className={
                                                        styles.rsTrafficChannelList
                                                    }
                                                >
                                                    {stations.length === 0 ? (
                                                        <span
                                                            className={
                                                                styles.rsScheduleItem
                                                            }
                                                        >
                                                            No traffic channels
                                                        </span>
                                                    ) : (
                                                        stations.map((s) => (
                                                            <ClassicyCheckbox
                                                                key={s.key}
                                                                id={`rs_traffic_${s.key}`}
                                                                label={s.label}
                                                                checked={
                                                                    !disabledTrafficChannels.has(
                                                                        s.key,
                                                                    )
                                                                }
                                                                onClickFunc={(
                                                                    checked: boolean,
                                                                ) =>
                                                                    setTrafficChannelEnabled(
                                                                        s.key,
                                                                        checked,
                                                                    )
                                                                }
                                                            />
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        <NowPlayingList
                                            segments={playingSegments}
                                            mutedItems={mutedItems}
                                            onToggleMute={toggleItemMute}
                                            soloItemId={soloItemId}
                                            onToggleSolo={toggleSoloItem}
                                        />
                                        {showSchedule && (
                                            <div style={{ display: "flex", flexDirection: "row", width: "100%", minHeight: "30%", maxHeight: "60%", gap: "var(--window-control-size)" }}>
                                                <div
                                                    className={
                                                        styles.rsScheduleSection
                                                    }
                                                >
                                                    <p
                                                        className={
                                                            styles.rsScheduleLabel
                                                        }
                                                    >
                                                        Coming Up
                                                    </p>
                                                        <ul
                                                            className={
                                                                styles.rsScheduleList
                                                            }
                                                        >
                                                        {upcomingList.length > 0 ?
                                                            upcomingList.map(
                                                                (item) => (
                                                                    <li
                                                                        key={
                                                                            item.id
                                                                        }
                                                                        className={
                                                                            styles.rsScheduleItem
                                                                        }
                                                                    >
                                                                        <img src={ClassicyIcons.controlPanels.soundManager.sound33} alt={item.title} />
                                                                        <span
                                                                            className={
                                                                                styles.rsCountdown
                                                                            }
                                                                        >
                                                                            {countdownLabel(
                                                                                item,
                                                                                nowMs,
                                                                            )}
                                                                        </span>
                                                                        {item.full_title ||
                                                                            item.title}
                                                                    </li>
                                                                ),
                                                            ) : <li className={styles.rsScheduleItem}>No upcoming items</li>}
                                                        </ul>
                                                </div>
                                                {previousList.length > 0 && (
                                                    <div
                                                        className={
                                                            styles.rsScheduleSection
                                                        }
                                                    >
                                                        <p
                                                            className={
                                                                styles.rsScheduleLabel
                                                            }
                                                        >
                                                            Previous
                                                        </p>
                                                        <ul
                                                            className={
                                                                styles.rsScheduleList
                                                            }
                                                        >
                                                            {previousList.map(
                                                                (item) => (
                                                                    <li
                                                                        key={
                                                                            item.id
                                                                        }
                                                                        className={
                                                                            styles.rsScheduleItem
                                                                        }
                                                                    >
                                                                        <img src={ClassicyIcons.controlPanels.soundManager.sound33} alt={item.title} />
                                                                        <span
                                                                            className={
                                                                                styles.rsCountdown
                                                                            }
                                                                        >
                                                                            {startTimeLabel(
                                                                                item,
                                                                                tzOffset,
                                                                            )}
                                                                        </span>
                                                                        <button
                                                                            type="button"
                                                                            className={
                                                                                styles.rsScheduleBtn
                                                                            }
                                                                            onMouseUp={() =>
                                                                                setFocusedItem(
                                                                                    item,
                                                                                )
                                                                            }
                                                                        >
                                                                            {item.full_title ||
                                                                                item.title}
                                                                        </button>
                                                                    </li>
                                                                ),
                                                            )}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <StationPlayer
                                        station={activeStationObj}
                                        nowMs={nowMs}
                                        getNowMs={getNowMs}
                                        stationMuted={false}
                                        mutedItems={playerMutedItems}
                                        clockPaused={clockPaused}
                                        showWaveform={showWaveform}
                                        captionsOn={captionsOn}
                                        captionStyle={settings.captionStyle}
                                        vizMode={settings.vizMode}
                                        onCycleVizMode={onCycleVizMode}
                                        waveColors={waveColors}
                                        maxVolume={maxVolume}
                                        playOriginalAudio={settings.playOriginalAudio}
                                    />
                                </>
                            )
                        )}
                    </div>

                    {/* Bottom row: control panel + one button per station */}
                    <div className={styles.rsBottomRow}>
                        <div className={styles.rsControlPanel}>
                            <ClassicyButton
                                buttonSize="small"
                                onClickFunc={() => setCaptionsOn((v) => !v)}
                                depressed={captionsOn}
                            >
                                {captionsOn ? "CC On" : "CC Off"}
                            </ClassicyButton>
                        </div>
                        <div className={styles.rsStationStrip}>
                            {sortedStations.map((station) => {
                                const isActive = station.key === activeStation;
                                return (
                                    <ClassicyButton
                                        key={station.key}
                                        depressed={isActive}
                                        onClickFunc={() => {
                                            setActiveStation(station.key);
                                            setFocusedItem(null);
                                        }}
                                    >
                                        <StationButtonContent
                                            label={station.label}
                                            status={stationStatus(
                                                station,
                                                upcomingItems,
                                                nowMs,
                                            )}
                                        />
                                    </ClassicyButton>
                                );
                            })}
                            {/* All Traffic: always last, aggregates every station. */}
                            <ClassicyButton
                                key={ALL_TRAFFIC_KEY}
                                depressed={isAllTraffic}
                                onClickFunc={() => {
                                    setActiveStation(ALL_TRAFFIC_KEY);
                                    setFocusedItem(null);
                                }}
                            >
                                <StationButtonContent
                                    label={ALL_TRAFFIC_LABEL}
                                    status={allTrafficStatus}
                                />
                            </ClassicyButton>
                        </div>
                    </div>
                </div>
            </ClassicyWindow>
            {aboutWindow}
        </ClassicyApp>
    );
};
